import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Star, Quote } from "lucide-react";
import { TESTIMONIALS } from "@/data/testimonials";

export default function Testimonials() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Student Reviews — The STEM Vet";

    // Inject JSON-LD Review + AggregateRating structured data.
    // Schema: a ProfessionalService with embedded reviews so Google can surface stars.
    const ld = {
      "@context": "https://schema.org",
      "@type": "ProfessionalService",
      "@id": "https://thestemvet.com/#business",
      name: "The STEM Vet",
      alternateName: "The STEM Vet Tutor",
      url: "https://thestemvet.com/",
      image: "https://thestemvet.com/branding/profile_square.png",
      description:
        "STEM tutoring, MMI coaching, admissions strategy, and essay preparation by Dr. Ashley Delmar, DVM, MRCVS. Supporting first-generation and non-traditional pre-health students.",
      priceRange: "$$",
      areaServed: "Worldwide",
      founder: {
        "@type": "Person",
        name: "Dr. Ashley Delmar",
        jobTitle: "Veterinarian & Academic Tutor",
      },
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: "5.0",
        bestRating: "5",
        worstRating: "1",
        reviewCount: String(TESTIMONIALS.length),
      },
      review: TESTIMONIALS.map((t) => ({
        "@type": "Review",
        reviewRating: {
          "@type": "Rating",
          ratingValue: "5",
          bestRating: "5",
          worstRating: "1",
        },
        author: { "@type": "Person", name: t.name },
        reviewBody: t.quote,
        itemReviewed: {
          "@type": "ProfessionalService",
          name: "The STEM Vet",
        },
      })),
    };

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-testid", "testimonials-jsonld");
    script.text = JSON.stringify(ld);
    document.head.appendChild(script);

    return () => {
      document.title = prevTitle;
      script.remove();
    };
  }, []);

  return (
    <div data-testid="testimonials-page" className="bg-paper-50">
      {/* HERO */}
      <section className="max-w-5xl mx-auto px-5 sm:px-8 pt-24 sm:pt-28 pb-14">
        <p className="overline text-terracotta" data-testid="testimonials-overline">
          Student voices
        </p>
        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-ink mt-4">
          Real results.{" "}
          <span className="text-forest">Real students.</span>
        </h1>
        <p className="mt-6 text-ink-muted text-lg leading-relaxed max-w-2xl">
          A collection of reviews from students I have worked with across STEM tutoring,
          MMI coaching, admissions strategy, and essay preparation.
        </p>
      </section>

      {/* REVIEWS — deterministic two-column split (evens / odds) for balanced layout */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[0, 1].map((col) => (
            <div key={col} className="flex flex-col gap-6">
              {TESTIMONIALS.filter((_, i) => i % 2 === col).map((t) => {
                const i = TESTIMONIALS.indexOf(t);
                return (
                  <figure
                    key={t.name}
                    data-testid={`testimonials-page-card-${i}`}
                    className="bg-paper-100 border border-paper-200 p-7 sm:p-8 rounded-sm relative"
                  >
                    <Quote
                      className="absolute -top-3 left-6 w-10 h-10 text-terracotta bg-paper-50 rounded-sm p-1.5"
                      strokeWidth={1.5}
                      aria-hidden
                    />
                    <div className="flex gap-0.5 text-scholar-dark mb-5 mt-2">
                      {[...Array(5)].map((_, k) => (
                        <Star key={k} className="w-4 h-4 fill-current" />
                      ))}
                    </div>
                    <blockquote className="text-ink leading-relaxed text-[15px] sm:text-base">
                      "{t.quote}"
                    </blockquote>
                    <figcaption className="mt-6 pt-5 border-t border-paper-200">
                      <p className="font-display font-bold text-ink">{t.name}</p>
                      <p className="text-sm text-ink-muted">{t.role}</p>
                    </figcaption>
                  </figure>
                );
              })}
            </div>
          ))}
        </div>
      </section>

      {/* CTA BAND */}
      <section className="bg-forest text-paper-50 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-24 relative z-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
          <div>
            <p className="overline text-scholar">Your turn</p>
            <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight max-w-2xl mt-3">
              Ready to write your own review?
            </h2>
            <p className="mt-5 text-paper-200/90 max-w-xl leading-relaxed">
              Book a free 20-minute intro call. We'll map out what comes next and see if we're a
              good fit.
            </p>
          </div>
          <Link
            to="/contact"
            data-testid="testimonials-cta"
            className="inline-flex items-center gap-2 bg-scholar text-ink px-7 py-4 text-base font-semibold rounded-sm hover:bg-scholar-dark hover:-translate-y-0.5 transition-all self-start"
          >
            Book your free intro call <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="absolute -right-20 -top-20 w-96 h-96 rounded-full bg-forest-700 opacity-50" aria-hidden />
        <div className="absolute -left-10 -bottom-20 w-72 h-72 rounded-full bg-terracotta opacity-20" aria-hidden />
      </section>
    </div>
  );
}
